import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap'
import { Trash2 } from 'lucide-react'
import {
  createBrandKeyword,
  deleteBrandKeyword,
  fetchBrandKeywords,
  fetchDivisions,
  updateBrandKeyword,
} from '../../services/api'

type BrandKeywordWhitelistProps = {
  /** When true, omit outer Card wrapper (e.g. inside MIM Setup tab). */
  embedded?: boolean
}

export default function BrandKeywordWhitelist({ embedded = false }: BrandKeywordWhitelistProps) {
  const qc = useQueryClient()
  const [newKeyword, setNewKeyword] = useState('')
  const [newDivisionIds, setNewDivisionIds] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDivisionIds, setEditDivisionIds] = useState<string[]>([])

  const { data: divisions = [] } = useQuery({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
  })

  const { data: brandKeywords = [], isLoading: keywordsLoading } = useQuery({
    queryKey: ['brand-keywords'],
    queryFn: fetchBrandKeywords,
  })

  const divisionNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const division of divisions) map.set(division.id, division.name)
    return map
  }, [divisions])

  const addMutation = useMutation({
    mutationFn: () => createBrandKeyword(newKeyword.trim(), newDivisionIds),
    onSuccess: () => {
      setNewKeyword('')
      setNewDivisionIds([])
      qc.invalidateQueries({ queryKey: ['brand-keywords'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, divisionIds }: { id: string; divisionIds: string[] }) =>
      updateBrandKeyword(id, { divisionIds }),
    onSuccess: () => {
      setEditingId(null)
      setEditDivisionIds([])
      qc.invalidateQueries({ queryKey: ['brand-keywords'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBrandKeyword(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brand-keywords'] }),
  })

  const toggleDivision = (ids: string[], divisionId: string, checked: boolean) => {
    if (checked) return [...new Set([...ids, divisionId])]
    return ids.filter((id) => id !== divisionId)
  }

  const renderDivisionCheckboxes = (selectedIds: string[], onChange: (ids: string[]) => void) => (
    <div className="d-flex flex-wrap gap-2">
      {divisions.map((division) => (
        <Form.Check
          key={division.id}
          type="checkbox"
          id={`brand-division-${division.id}`}
          label={division.name}
          checked={selectedIds.includes(division.id)}
          onChange={(event) => onChange(toggleDivision(selectedIds, division.id, event.target.checked))}
        />
      ))}
    </div>
  )

  const body = (
    <>
      <p className="text-muted small mb-3">
        Brand names used when scanning distributor websites. Assign each brand to one or more divisions so teams only
        discover products relevant to their market — a division will not use another division&apos;s brands unless
        explicitly selected.
      </p>

      <Form
        className="mb-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (newKeyword.trim() && newDivisionIds.length > 0) addMutation.mutate()
        }}
      >
        <Form.Label className="small fw-semibold">Add brand</Form.Label>
        <div className="d-flex flex-wrap gap-2 mb-2">
          <Form.Control
            placeholder="Brand name (e.g. Kingspan)"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            style={{ maxWidth: 320 }}
            aria-label="New brand keyword"
          />
          <Button variant="primary" disabled={!newKeyword.trim() || newDivisionIds.length === 0 || addMutation.isPending} type="submit">
            {addMutation.isPending ? 'Adding…' : 'Add brand'}
          </Button>
        </div>
        <Form.Label className="small text-muted">Divisions</Form.Label>
        {divisions.length === 0 ? (
          <div className="text-muted small">No divisions configured.</div>
        ) : (
          renderDivisionCheckboxes(newDivisionIds, setNewDivisionIds)
        )}
      </Form>

      {addMutation.isError && (
        <Alert variant="danger" className="py-2 mb-3">
          Could not add that brand. It may already exist or divisions may be invalid.
        </Alert>
      )}

      {keywordsLoading ? (
        <div className="text-muted">Loading brand whitelist…</div>
      ) : brandKeywords.length === 0 ? (
        <div className="text-muted">No brand names configured yet.</div>
      ) : (
        <Table size="sm" striped hover responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Brand name</th>
              <th>Divisions</th>
              <th>Status</th>
              <th className="text-end" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {brandKeywords.map((k) => (
              <tr key={k.id}>
                <td>
                  <strong>{k.keyword}</strong>
                </td>
                <td>
                  {editingId === k.id ? (
                    <div className="py-1">
                      {renderDivisionCheckboxes(editDivisionIds, setEditDivisionIds)}
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={editDivisionIds.length === 0 || updateMutation.isPending}
                          onClick={() => updateMutation.mutate({ id: k.id, divisionIds: editDivisionIds })}
                        >
                          Save divisions
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {(k.divisionIds ?? []).length === 0 ? (
                        <span className="text-muted small">None</span>
                      ) : (
                        k.divisionIds.map((id) => (
                          <Badge key={id} bg="light" text="dark" className="border">
                            {divisionNameById.get(id) ?? id}
                          </Badge>
                        ))
                      )}
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 ms-1"
                        onClick={() => {
                          setEditingId(k.id)
                          setEditDivisionIds(k.divisionIds ?? [])
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </td>
                <td>
                  <Badge bg={k.isActive ? 'success' : 'secondary'}>{k.isActive ? 'Active' : 'Inactive'}</Badge>
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(k.id)}
                    title={`Remove ${k.keyword}`}
                    aria-label={`Remove ${k.keyword}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  )

  if (embedded) {
    return <div data-tour="mim-brand-whitelist">{body}</div>
  }

  return (
    <Card className="mb-4" data-tour="mim-brand-whitelist">
      <Card.Body>
        <Card.Title>Brand keyword whitelist</Card.Title>
        {body}
      </Card.Body>
    </Card>
  )
}
